import { createStyles } from '@mantine/core';

import { MENU_BREAKPOINT } from '~/domains/common/hooks/mediaQueries';

import { HEADER_HEIGHT, SUB_HEADER_HEIGHT } from '../../constants';

const SCREEN_WIDTH_XXXL = 1486;

const useStyles = createStyles((theme) => ({
  headerContainer: {
    position: 'sticky',
    top: 0,
    width: '100%',
    height: HEADER_HEIGHT,
    zIndex: theme.other.zIndex.header,
  },
  subHeaderContainer: {
    position: 'relative',
    height: SUB_HEADER_HEIGHT,
  },
  headerContent: {
    position: 'relative',
    zIndex: 1,
    color: theme.white,
    display: 'flex',
    padding: `0 ${theme.other.spacing._24}px`,
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    height: '100%',
    margin: '0 auto',

    [theme.fn.smallerThan('sm')]: {
      padding: `0 ${theme.other.spacing._16}px`,
    },
  },
  svgBgr: {
    display: 'block',
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    zIndex: 0,
  },

  logoContainer: {
    display: 'flex',
    alignItems: 'center',
    flexShrink: 0,
    gap: /* eslint-disable-line local-rules/use-theme-tokens */ 5,
  },

  navContainer: {
    marginTop: 'auto',
    marginLeft: theme.other.spacing._32,
    marginRight: 'auto',
    display: 'flex',
    alignItems: 'stretch',
    height: '100%',
    gap: theme.other.spacing._16,

    [theme.fn.smallerThan(MENU_BREAKPOINT)]: {
      display: 'none',
    },

    [theme.fn.smallerThan(SCREEN_WIDTH_XXXL)]: {
      gap: theme.other.spacing._8,
    },
  },
  navLink: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: theme.white,
    textDecoration: 'none',
    padding: `0 ${theme.other.spacing._16}px`,
    borderTop: `2px solid transparent`,
    borderBottom: `2px solid transparent`,
    transition: 'border-color 0.2s ease-in-out',

    '&:hover': {
      borderTopColor: 'rgba(75, 235, 226, 0.5)',
      color: theme.colors.blue[4],
    },

    '&.active': {
      color: theme.colors.blue[4],
      borderTopColor: theme.colors.blue[4],
    },
  },

  userContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.other.spacing._12,
  },
  searchButton: {
    width: 36,
    height: 36,
    borderRadius: /* eslint-disable-line local-rules/use-theme-tokens */ 36,
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'background-color 0.2s ease-in-out',

    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.2)',
    },
  },
  promotionsActive: {
    backgroundColor: 'rgba(75, 235, 226, 0.1)',
    '&:hover': {
      backgroundColor: 'rgba(75, 235, 226, 0.25)',
    },
  },

  primaryButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: /* eslint-disable-line local-rules/use-theme-tokens */ '0 12px',
    gap: theme.other.spacing._6,
    height: 36,
    borderRadius: /* eslint-disable-line local-rules/use-theme-tokens */ 36,
    backgroundColor: theme.colors.blue[4],
    textDecoration: 'none',
    color: theme.colors.gray2[6],
    transition: 'background-color 0.2s ease-in-out, box-shadow 0.2s ease-in-out',

    '&::not(:disabled):hover': {
      backgroundColor: theme.other.color.blue.hover,
      boxShadow: '0px 4px 16px 0px rgba(75, 235, 226, 0.3)',
    },
  },
  authButton: {},
  cacButton: {
    [theme.fn.smallerThan(SCREEN_WIDTH_XXXL)]: {
      width: 36,
      padding: 0,

      '& > span': {
        display: 'none',
      },
    },
  },
  cacButtonAnonymous: {
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    color: theme.white,

    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.2)',
      boxShadow: 'none',
    },

    [theme.fn.smallerThan('md')]: {
      display: 'none',
    },
  },
  divider: {
    width: 1,
    height: 24,
    backgroundColor: theme.colors.gray2[4],

    [theme.fn.smallerThan(SCREEN_WIDTH_XXXL)]: {
      display: 'none',
    },
  },

  userWidgetContainer: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    paddingLeft: theme.other.spacing._4,
    paddingRight: theme.other.spacing._8,
    gap: theme.other.spacing._6,
    height: 36,
    borderRadius: /* eslint-disable-line local-rules/use-theme-tokens */ 36,
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    transition: 'background-color 0.2s ease-in-out',
    cursor: 'pointer',

    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.2)',
    },
  },
  notificationBadge: {
    position: 'absolute',
    top: -3,
    left: 20,
    width: 16,
    height: 16,
    borderRadius: theme.radius.lg,
    color: theme.white,
    backgroundColor: theme.colors.red[4],
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',

    '&.isWide': {
      left: 17,
      width: 20,
    },
    '&.isCapped': {
      left: 15,
      width: 26,
    },
  },
  balance: {
    [theme.fn.smallerThan('sm')]: {
      display: 'none',
    },
  },
  balanceSkeleton: {
    opacity: 0.2,
    width: 50,
    height: 14,
    borderRadius: /* eslint-disable-line local-rules/use-theme-tokens */ 12,
  },
  depositButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 24,
    height: 24,
    borderRadius: /* eslint-disable-line local-rules/use-theme-tokens */ 24,
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    transition: 'background-color 0.2s ease-in-out',

    '&:hover': {
      backgroundColor: theme.white,

      '& > svg path': {
        stroke: theme.colors.gray2[7],
      },
    },

    [theme.fn.smallerThan('sm')]: {
      display: 'none',
    },
  },
  mobileMenuButton: {
    display: 'none',
    width: 24,
    height: 24,
    borderRadius: /* eslint-disable-line local-rules/use-theme-tokens */ 24,
    alignItems: 'center',
    justifyContent: 'center',
    color: theme.white,

    [theme.fn.smallerThan(MENU_BREAKPOINT)]: {
      display: 'flex',
    },

    '&.standalone': {
      width: 36,
      height: 36,
      borderRadius: /* eslint-disable-line local-rules/use-theme-tokens */ 36,
      background: 'rgba(255, 255, 255, 0.1)',

      '&:hover': {
        background: 'rgba(255, 255, 255, 0.2)',
      },
    },
  },
  promotionsButtonContainer: {
    position: 'relative',

    [theme.fn.smallerThan('md')]: {
      display: 'none',
    },
  },
  promotionsBar: {
    position: 'absolute',
    top: -14,
    left: '50%',
    transform: 'translateX(-50%)',
    width: 36,
    height: 2,
    backgroundColor: theme.colors.blue[4],
    zIndex: theme.other.zIndex.header + 1,
    opacity: 0,
    transition: 'opacity 0.2s ease-in-out',
  },
  promotionsBarActive: {
    opacity: 1,
  },
}));

export default useStyles;
